import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import history from "../../history";
import { onLogin, onLogout, onResetLoading } from "../../State/actions/user";
import { showLocalizedToaster } from "../../State/actions/toaster";
import { getServerRegions } from "../../config";
import userService from "../../services/userService";
import Text from "../../components/Text";
import Messages from "../../Shared/Messages";
import {
  PUBLIC_CHANNEL_PASSWORD,
  getLocalPublicChannelApiUrl,
  getPublicChannelLanding,
  isLoggedInAs,
} from "./publicChannelLink";
import "./PublicChannelLogin.scss";

// Long enough to still be on screen once the login page has rendered.
const ERROR_TOASTER_SECONDS = 7;

const PHASE_STARTING = "starting";
const PHASE_PENDING = "pending";

// Points this session at the backend the link should use (see
// getLocalPublicChannelApiUrl), through the same call the login page's region
// selector makes: axios for the requests that follow, and the stored API URL so
// a reload resolves the same backend.
const selectPublicChannelBackend = () => {
  const url = getLocalPublicChannelApiUrl(
    window.location.hostname,
    getServerRegions()
  );
  if (url) {
    userService.setServerURL(url);
  }
};

// /a/p/:patientId — a public channel link. Signs the visitor in as the channel's
// account behind the scenes through the ordinary LOG_IN saga, then hands over to
// the app's own role-based landing. Anything other than a successful login sends
// the visitor to the login page with an error toaster. Only a status screen is
// rendered here.
const PublicChannelLogin = ({ match }) => {
  const username = match.params.patientId;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  // PHASE_PENDING is set through state, not a ref, on purpose: it re-renders, so
  // the outcome effect below reads the user slice as it is *after* the login was
  // dispatched. Reading the mount-render snapshot instead would mistake a stale
  // loginError, left by an earlier failed sign-in, for this one's result.
  const [phase, setPhase] = useState(PHASE_STARTING);
  // One-shot hand-off: never two redirects, never two toasters.
  const settled = useRef(false);

  const settle = (path, failureMessage) => {
    if (settled.current) {
      return;
    }
    settled.current = true;
    if (failureMessage) {
      dispatch(
        showLocalizedToaster(
          { content: failureMessage },
          "error",
          ERROR_TOASTER_SECONDS
        )
      );
    }
    history.replace(path);
  };

  // Kick off exactly once on mount.
  useEffect(() => {
    if (isLoggedInAs(user, username)) {
      settle(getPublicChannelLanding(user.role));
      return;
    }
    if (user.loggedIn) {
      // Another account is active: end that session first so the link's account
      // takes over. skipAggressiveClear — the reducers reset on LOG_OUT anyway,
      // and the storage purge would race the new session's persistence.
      dispatch(onLogout(undefined, true));
    } else {
      // Same clean slate the login page gives itself on mount.
      dispatch(onResetLoading());
    }
    // After the logout above, whose request must still reach the old backend,
    // and before the login, which must reach the new one.
    selectPublicChannelBackend();
    dispatch(onLogin(username, PUBLIC_CHANNEL_PASSWORD, false));
    setPhase(PHASE_PENDING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow the saga's outcome through the user slice.
  useEffect(() => {
    if (phase !== PHASE_PENDING || user.loading) {
      return;
    }
    if (user.loggedIn) {
      settle(getPublicChannelLanding(user.role));
    } else if (user.loginError || user.mfaRequired) {
      // mfaRequired lands here too. These accounts are not expected to carry a
      // second factor, and the login page resets its own state on mount
      // (onResetLoading), so it could not resume a half-finished MFA step.
      settle("/login", Messages.public_link_login_failed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phase,
    user.loggedIn,
    user.loading,
    user.loginError,
    user.mfaRequired,
    user.role,
  ]);

  return (
    <main id="main-layout" className="public-channel-login">
      <i
        className="fa fa-spinner fa-spin public-channel-login__icon"
        aria-hidden="true"
      />
      <h1 className="public-channel-login__title">
        <Text content={Messages.entra_signing_in} />
      </h1>
      <p className="public-channel-login__text">
        <Text content={Messages.entra_verifying_credentials} />
      </p>
    </main>
  );
};

export default PublicChannelLogin;
